import {Injectable} from '@angular/core';
import {DocumentInterruptSource, Idle} from '@ng-idle/core';
import {Keepalive} from '@ng-idle/keepalive';
import {BehaviorSubject, Subject} from 'rxjs';
import {AdminService} from './admin.service';
import {UrlService} from './url.service';
import {SessionStorageService} from './support/session-storage.service';

/**
 * Coordinates idle detection, warning countdown, keepalive, and logout for the
 * Angular client. Mirrors the design in the Router/Client-Js AngularJS apps
 * (idleServiceProvider.js + sessionExpirationController.js) and the GWT app
 * (SessionTimeoutManager.java) and uses the same `sharedUserActivity`
 * localStorage key so all four webapps participate in cross-tab coordination
 * over the same origin.
 */
@Injectable()
export class SessionExpirationService {
    private static readonly SHARED_ACTIVITY_KEY = 'sharedUserActivity';
    private static readonly BROADCAST_DEBOUNCE_MS = 15000;

    /** True while the warning modal should be visible. */
    public readonly warningVisible$ = new BehaviorSubject<boolean>(false);
    /** Seconds remaining until forced logout, while in the warning state. */
    public readonly secondsRemaining$ = new BehaviorSubject<number>(0);
    /** Emits when the user comes back from idle while the warning was up. */
    public readonly keptAlive$ = new Subject<void>();

    private lastBroadcast = 0;
    private storageListener?: (e: StorageEvent) => void;
    private visibilityListener?: () => void;

    constructor(private idle: Idle,
                private keepalive: Keepalive,
                private adminService: AdminService,
                private urlService: UrlService,
                private sessionStorageService: SessionStorageService) {
    }

    /**
     * Wire up idle/keepalive using thresholds from the application configuration
     * and start watching. Safe to call once after configuration has loaded.
     */
    start(applicationConfiguration: any): void {
        const totalIdleSeconds = applicationConfiguration.sessionIdleTimeoutIssuedSeconds;
        const warningAtSeconds = applicationConfiguration.sessionIdleWarningIssuedSeconds;
        const keepaliveSeconds = applicationConfiguration.sessionKeepaliveInterval;

        // ng-idle semantics:
        //   setIdle(s)    = how long the user must be idle before onIdleStart fires (warning point)
        //   setTimeout(s) = how long the warning countdown runs before onTimeout fires (logout)
        // The JNDI values are absolute "seconds since last activity" thresholds, so the
        // countdown duration is (timeout - warning).
        this.idle.setIdle(warningAtSeconds);
        this.idle.setTimeout(Math.max(1, totalIdleSeconds - warningAtSeconds));

        // Intentional input only — matches the AngularJS/GWT versions, which deliberately
        // exclude mousemove so a wandering cursor doesn't keep someone logged in.
        this.idle.setInterrupts([
            new DocumentInterruptSource('keydown click touchstart')
        ]);

        this.idle.onIdleStart.subscribe(() => {
            this.warningVisible$.next(true);
        });

        this.idle.onTimeoutWarning.subscribe((seconds: number) => {
            this.secondsRemaining$.next(seconds);
        });

        this.idle.onIdleEnd.subscribe(() => {
            this.warningVisible$.next(false);
            this.secondsRemaining$.next(0);
            this.keptAlive$.next();
        });

        this.idle.onTimeout.subscribe(() => {
            this.warningVisible$.next(false);
            this.logout();
        });

        // Whenever ng-idle sees local activity, broadcast it to sibling tabs (debounced).
        this.idle.onInterrupt.subscribe(() => this.broadcastActivity());

        // Phase A keepalive lives here now: ping the server on an interval to keep
        // the Tomcat HttpSession from expiring while the user is idle on a single page.
        this.keepalive.interval(keepaliveSeconds);
        this.keepalive.onPing.subscribe(() => this.adminService.keepAlive());

        // Listen for activity broadcast from other tabs / Router / GWT.
        this.storageListener = (e: StorageEvent) => {
            if (e.key === SessionExpirationService.SHARED_ACTIVITY_KEY) {
                // External activity — treat as a local interrupt so the idle timer resets
                // and the warning modal closes if it happened to be open.
                this.idle.interrupt();
            }
        };
        window.addEventListener('storage', this.storageListener);

        // Fire an immediate keepalive when the tab becomes visible again. Closes the gap
        // where background-tab throttling or full tab freezing stops pings from firing
        // on schedule, which can let the server session expire before the user returns.
        this.visibilityListener = () => {
            if (document.visibilityState === 'visible') {
                this.adminService.keepAlive();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityListener);

        this.idle.watch();
        this.keepalive.start();
    }

    /** Called from the warning modal's "Stay logged in" button. */
    stayLoggedIn(): void {
        this.idle.watch();
        this.warningVisible$.next(false);
        this.secondsRemaining$.next(0);
        // Explicitly broadcast to sibling tabs/apps so they reset their idle timers
        // too. Bypass the debounce — this is an intentional, user-initiated signal,
        // and we want it to fire immediately even if onInterrupt happened to broadcast
        // moments ago.
        this.lastBroadcast = 0;
        this.broadcastActivity();
    }

    logout(): void {
        // Drop any cached infinite-table filters so the next user on this browser
        // doesn't inherit them.
        this.sessionStorageService.clearFilterStorage();
        // Matches the top-bar Logout button. Resolved against the backend base URL
        // so it works in dev (Tomcat on a different port than ng serve) as well as
        // in production (same origin as the SPA).
        window.location.href = this.urlService.getBackendUrl() + 'j_spring_security_logout';
    }

    private broadcastActivity(): void {
        const now = Date.now();
        if (now - this.lastBroadcast > SessionExpirationService.BROADCAST_DEBOUNCE_MS) {
            // Deliberately raw localStorage (not SessionStorageService): the cross-tab
            // 'storage' event only fires for localStorage changes, and we need siblings
            // tabs/apps (Router, Client-Js, GWT) to see this heartbeat.
            localStorage.setItem(SessionExpirationService.SHARED_ACTIVITY_KEY, now.toString());
            this.lastBroadcast = now;
        }
    }
}
